import { useCallback, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'

import type { RequestTrendStatus, Trend, TrendRelatedTerritory, TrendRelevance, TrendStatus } from 'entities/trend'
import type { GetTrendsIdsByCategoryQuery, GetTrendTerritoriesByIdsQuery } from 'generated/graphql'
import { useTranslate } from 'lib/tolgee'
import { datadog } from 'utils'

import { GET_TRENDS_ESSENTIAL_BY_CATEGORY_LOG } from './consts'
import { GET_TRENDS_BASE_BY_CATEGORY_QUERY, GET_TREND_TERRITORIES_BY_IDS_QUERY } from './operations'
import { getTrendsDescriptionsMap } from './utils'

export const useTrendTerritories = () => {
  const t = useTranslate()

  const {
    data: trendsBaseData,
    loading: isTrendsBaseLoading,
    error: trendsBaseError,
    called: isTrendsBaseCalled,
    variables: trendsEssentialApolloVariables,
  } = useQuery<GetTrendsIdsByCategoryQuery>(GET_TRENDS_BASE_BY_CATEGORY_QUERY)

  // TODO: add utility to get territoryId
  const trendTerritoriesIds = trendsBaseData?.getTrendingHome?.data?.map((trend: any) => `trending.${trend?.id}`) || []

  const {
    data: trendTerritoriesData,
    loading: isTrendTerritoriesLoading,
    error: trendTerritoriesError,
    called: isTrendTerritoriesCalled,
    variables: trendTerritoriesApolloVariables,
  } = useQuery<GetTrendTerritoriesByIdsQuery>(GET_TREND_TERRITORIES_BY_IDS_QUERY, {
    skip: !trendsBaseData?.getTrendingHome?.data,
    variables: {
      ids: trendTerritoriesIds,
    },
  })

  const handleTrendEssentialsErrors = useCallback(() => {
    if (!isTrendsBaseCalled || isTrendsBaseLoading || !trendsBaseError) return

    datadog.dispatchRequestErrorLog({
      log: GET_TRENDS_ESSENTIAL_BY_CATEGORY_LOG,
      query: { name: 'getTrendsIdsByCategory', variables: trendsEssentialApolloVariables },
      error: trendsBaseError,
    })
  }, [isTrendsBaseCalled, isTrendsBaseLoading, trendsBaseError, trendsEssentialApolloVariables])

  const handleTrendTerritoriesErrors = useCallback(() => {
    if (!isTrendTerritoriesCalled || isTrendTerritoriesLoading || !trendTerritoriesError) return

    datadog.dispatchRequestErrorLog({
      log: GET_TRENDS_ESSENTIAL_BY_CATEGORY_LOG,
      query: { name: 'getTrendTerritoriesByIds', variables: trendTerritoriesApolloVariables },
      error: trendTerritoriesError,
    })
  }, [isTrendTerritoriesCalled, isTrendTerritoriesLoading, trendTerritoriesError, trendTerritoriesApolloVariables])

  useEffect(handleTrendEssentialsErrors, [handleTrendEssentialsErrors])
  useEffect(handleTrendTerritoriesErrors, [handleTrendTerritoriesErrors])

  const requestTrendStatus: RequestTrendStatus = trendsBaseData?.getTrendingHome?.status

  const relatedTerritory = useMemo<TrendRelatedTerritory>(() => {
    const relatedTerritoryData = trendsBaseData?.getTrendingHome?.relatedTerritory

    if (!relatedTerritoryData?.territoryId) {
      return null
    }

    return {
      territoryId: relatedTerritoryData?.territoryId,
      name: relatedTerritoryData?.name,
    }
  }, [trendsBaseData?.getTrendingHome?.relatedTerritory])

  const trendTerritories = useMemo<Trend[]>(() => {
    const trendsData = Array.from(trendsBaseData?.getTrendingHome?.data || [])
    const trendTerritoriesList = (trendTerritoriesData?.listTerritories || []).filter(
      territory => territory.__typename === 'Trending'
    )

    const parsedTrends = trendTerritoriesList.map(trendTerritory => {
      const metrics = trendTerritory?.territoryPerformance?.metrics

      const trendEssentialData = trendsData.find(trend => String(trendTerritory?.territoryId).includes(trend?.id))

      const relevanceData = JSON.parse(trendEssentialData?.relevance) || []
      const parsedRelevance: TrendRelevance[] = relevanceData.map(relevance => ({
        relevance: relevance?.relevance,
        postCount: relevance?.post_count,
        publishedAt: relevance?.published_at,
      }))

      return {
        id: trendEssentialData?.id,
        name: trendTerritory?.name,
        status: trendEssentialData?.status as TrendStatus,
        query: trendTerritory?.query,
        filtersObject: trendEssentialData?.filtersObject,
        totalPosts: trendEssentialData?.totalPosts,
        relevance: parsedRelevance,
        metrics,
        createdAt: trendTerritory?.createdAt,
      }
    })

    const descriptionsMap = getTrendsDescriptionsMap(t, parsedTrends)
    const trendsWithDescription = parsedTrends.map(trend => {
      const description = descriptionsMap.get(trend.id)
      return { ...trend, description }
    })

    return trendsWithDescription
  }, [trendsBaseData?.getTrendingHome?.data, trendTerritoriesData?.listTerritories, t])

  const isLoading = isTrendsBaseLoading || isTrendTerritoriesLoading
  const error = trendsBaseError || trendTerritoriesError

  const memoizedValues = useMemo(
    () => ({
      status: requestTrendStatus,
      relatedTerritory,
      trendTerritories,
      isLoading,
      error,
    }),
    [relatedTerritory, trendTerritories, isLoading, error, requestTrendStatus]
  )

  return memoizedValues
}
